import React, { JSX, useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import produce from "immer";
import { LoadingButton } from "@mui/lab";
import Panel from "../../components/Panel";
// @ts-expect-error - css module types may be missing
import styles from "./ServiceProvider.module.css";
import ServiceCategoryAutoSelect from "../Services/ServiceCategoryAutoSelect";
import { SERVICE_PROVIDER_RATE_UNIT } from "../Calendar/Booking/ServicesSelectWithSpAssign";
import List from "../../components/List";
import { tl } from "../../components/translate";
import { ServiceInterface } from "../../interfaces/ServiceInterface";
import { patchBatchServiceRates } from "../../api/services";
import { notificationAdd } from "../../actions/notification";
import { useAppDispatch } from "../../store/hooks";
import { getServices } from "../../actions";
import { ServiceProvider } from "../../interfaces/ServiceProvidersInterface";
import { spFullNameSelector } from "../../reducers/serviceProvider";

interface Props {
  onClose: () => void;
  services: ServiceInterface[];
  spId: number;
  sp: ServiceProvider;
}

function SetBulkCommissionRatePanel({ onClose, services, spId, sp }: Props): JSX.Element {
  const [data, setData] = useState<ServiceInterface[]>(services);
  const [selectedCategory, setSelectedCategory] = useState("procedures");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const dispatch = useAppDispatch();
  const [bulkData, setBulkData] = useState({ unit: "", rate: "" });

  const handleSubmit = async () => {
    setIsSubmitted(true);
    const selectedIdSet = new Set(selectedIds.map(Number));

    const formData = data
      .filter((item) => selectedIdSet.has(Number(item.id)))
      .map((item) => ({
        id: item.id,
        serviceProviderRateUnit: item.serviceProviderRateUnit,
        document: structuredClone(item.document)
      }));

    try {
      const res = await patchBatchServiceRates(formData);

      dispatch(getServices());
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "success",
          message: res.message,
          autoTimeout: true
        })
      );
    } catch (e: any) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: e.message || "Something went wrong, Please try again.",
          autoTimeout: true
        })
      );
    } finally {
      setIsSubmitted(false);
    }
  };

  return (
    <Panel onClose={onClose} titleJsx={<Typography flex={1}>{spFullNameSelector(sp)}</Typography>}>
      <Box px={4} width="100%" className={styles.boxHeight}>
        <ServiceCategoryAutoSelect
          value={selectedCategory}
          onChange={(value) => {
            setSelectedCategory(value);
          }}
          variant="outlined"
        />
        <Box height="85vh" mt={1}>
          <List
            multiSelectContextHeader={
              <LoadingButton loading={isSubmitted} onClick={handleSubmit} variant="contained">
                Update Selected
              </LoadingButton>
            }
            data={data.filter((item) => item.category === selectedCategory)}
            multiSelectable
            onMultiSelect={setSelectedIds}
            withoutSearch
            customButtonGroup={
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <TextField
                  select
                  size="small"
                  sx={{ width: 120 }}
                  variant="outlined"
                  label="Bulk unit"
                  value={bulkData.unit}
                  onChange={(event) => {
                    const value = event.target.value as SERVICE_PROVIDER_RATE_UNIT;
                    setBulkData((prevState) => ({ ...prevState, unit: value }));

                    setData(
                      produce((draft) => {
                        draft.forEach((draftItem) => {
                          draftItem.serviceProviderRateUnit = value;
                        });
                      })
                    );
                  }}
                >
                  <MenuItem value={SERVICE_PROVIDER_RATE_UNIT.RUPEE}>
                    {SERVICE_PROVIDER_RATE_UNIT.RUPEE}
                  </MenuItem>

                  <MenuItem value={SERVICE_PROVIDER_RATE_UNIT.PERCENTAGE}>
                    {SERVICE_PROVIDER_RATE_UNIT.PERCENTAGE}
                  </MenuItem>
                </TextField>
                <TextField
                  size="small"
                  variant="outlined"
                  sx={{ width: 120 }}
                  value={bulkData.rate}
                  label="Bulk rate"
                  onChange={(event) => {
                    const { value } = event.target;
                    setBulkData((prevState) => ({ ...prevState, rate: value }));
                    setData(
                      produce((draft) => {
                        draft.forEach((draftItem) => {
                          if (!draftItem.document) {
                            draftItem.document = { rates: [] };
                          }

                          const rates = draftItem.document.rates ?? [];

                          const existingRate = rates.find((r) => r.serviceProviderId === spId);

                          if (existingRate) {
                            // update
                            existingRate.rate = value;
                          } else {
                            // insert
                            rates.push({
                              serviceProviderId: spId,
                              rate: value
                            });
                          }

                          draftItem.document.rates = rates;
                        });
                      })
                    );
                  }}
                />
              </Box>
            }
            hideCreateButton
            defaultSortColumn="name"
            columns={[
              {
                key: "name",
                label: tl("name"),
                sortable: true,
                sortFunction: (a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1),
                formatter: (item) => (
                  <Typography>
                    {item.name}
                    {item.subType ? ` (${item.subType})` : ""}
                  </Typography>
                )
              },
              {
                key: "grossTotalPrice",
                label: tl("price"),
                formatter: (item) => (
                  <Typography
                    sx={{
                      maxWidth: "100%",
                      width: "100px"
                    }}
                  >
                    {tl("rs")}
                    {item.grossTotalPrice}
                  </Typography>
                )
              },
              {
                key: "serviceProviderRateUnit",
                label: "Rate unit",
                formatter: (row) => (
                  <TextField
                    select
                    size="small"
                    variant="outlined"
                    value={row.serviceProviderRateUnit ?? ""}
                    onChange={(event) => {
                      const value = event.target.value as SERVICE_PROVIDER_RATE_UNIT;

                      setData(
                        produce((draft) => {
                          const rowData = draft.find((item) => item.id === row.id);
                          if (!rowData) return;

                          rowData.serviceProviderRateUnit = value;
                        })
                      );
                    }}
                  >
                    <MenuItem value={SERVICE_PROVIDER_RATE_UNIT.RUPEE}>
                      {SERVICE_PROVIDER_RATE_UNIT.RUPEE}
                    </MenuItem>

                    <MenuItem value={SERVICE_PROVIDER_RATE_UNIT.PERCENTAGE}>
                      {SERVICE_PROVIDER_RATE_UNIT.PERCENTAGE}
                    </MenuItem>
                  </TextField>
                )
              },
              {
                key: "rate",
                label: "Rate",
                formatter: (row) => {
                  const spRate = row.document?.rates?.find(
                    (item) => item.serviceProviderId === spId
                  );

                  return (
                    <TextField
                      size="small"
                      variant="outlined"
                      // sx={{ width: 60 }}
                      value={spRate?.rate ?? ""}
                      onChange={(event) => {
                        const { value } = event.target;

                        setData(
                          produce((draft) => {
                            const rowData = draft.find((item) => item.id === row.id);
                            if (!rowData) return;

                            if (!rowData.document) {
                              rowData.document = { rates: [] };
                            }

                            const rates = rowData.document.rates ?? [];

                            const existingRate = rates.find((r) => r.serviceProviderId === spId);

                            if (existingRate) {
                              // update
                              existingRate.rate = value;
                            } else {
                              // insert
                              rates.push({
                                serviceProviderId: spId,
                                rate: value
                              });
                            }

                            rowData.document.rates = rates;
                          })
                        );
                      }}
                    />
                  );
                }
              }
            ]}
          />
        </Box>
      </Box>
    </Panel>
  );
}

export default SetBulkCommissionRatePanel;
